import { dateFormatters } from '@/utils/date-util'
import { formatMoney } from '@/utils/money-util'
import { type TableSortOrder } from '@/components/Table'
import {
  ClickableTable,
  ClickableTableHead,
  ClickableTableHeader,
  ClickableTableBody,
  ClickableTableRow,
  ClickableTableCell,
} from '@/components/Table/ClickableTable'
import { Pill } from '@/components/Pill'
import styles from './AmendmentsTable.module.css'
import cx from 'classnames'

export interface AmendmentsTableColumn<K extends string> {
  key: K
  header: string
  sortable?: boolean
}

export interface AmendmentsTableRow {
  description: string
  internalNote: string
  visibilityText: string
  customerAmount: number
  providerAmount: number
  dateCreated: Date
}

export interface AmendmentsTableProps<K extends string> {
  columns: AmendmentsTableColumn<K>[]
  currentSort: K
  currentSortOrder: TableSortOrder
  onSort: (key: K, sortOrder: TableSortOrder) => unknown
  amendments: AmendmentsTableRow[]
}

export function AmendmentsTable<K extends string>({
  columns,
  currentSort,
  currentSortOrder,
  onSort,
  amendments,
}: AmendmentsTableProps<K>) {
  return (
    <div className={styles.tableContainer}>
      <ClickableTable stickyHeader>
        <ClickableTableHead>
          {columns.map((col) => (
            <ClickableTableHeader
              key={col.key}
              canSort={col.sortable}
              sortOrder={col.key === currentSort ? currentSortOrder : 'none'}
              onSort={(newSort) => onSort(col.key, newSort)}
            >
              <div className={styles.tableHeader}>{col.header}</div>
            </ClickableTableHeader>
          ))}
        </ClickableTableHead>
        <ClickableTableBody>
          {amendments.map((amendment, i) => {
            const isCustomerAmountNegative = amendment.customerAmount < 0
            const isProviderAmountNegative = amendment.providerAmount < 0

            return (
              <ClickableTableRow key={i}>
                <ClickableTableCell>
                  <div className={styles.description}>
                    {amendment.description}
                  </div>
                  <div className={styles.internalNote}>
                    {amendment.internalNote}
                  </div>
                </ClickableTableCell>
                <ClickableTableCell>
                  <Pill>{amendment.visibilityText}</Pill>
                </ClickableTableCell>
                <ClickableTableCell
                  className={cx(
                    styles.amount,
                    isCustomerAmountNegative && styles.negativeAmount,
                  )}
                >
                  {formatMoney(amendment.customerAmount)}
                </ClickableTableCell>
                <ClickableTableCell
                  className={cx(
                    styles.amount,
                    isProviderAmountNegative && styles.negativeAmount,
                  )}
                >
                  {formatMoney(amendment.providerAmount)}
                </ClickableTableCell>
                <ClickableTableCell className={styles.date}>
                  {dateFormatters.dateTimeDefault.format(amendment.dateCreated)}
                </ClickableTableCell>
              </ClickableTableRow>
            )
          })}
        </ClickableTableBody>
      </ClickableTable>
    </div>
  )
}
